import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Info, LoaderCircle, MoreHorizontal, RefreshCw, Trash2, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { deleteAllMemoriesApi, refreshMemorySummaryApi, updateMemorySettingsApi } from '../../api/memory.api';
import { Button } from '../../components/ui/Button';
import { useAppDispatch } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { fetchMemorySettings } from '../../redux/slices/memorySlice';
import { classNames } from '../../utils/classNames';
import { MEMORY_SUMMARY_MAX } from './personalizationConstants';
import { ConfirmDangerDialog } from './ConfirmDangerDialog';

const relativeUpdatedLabel = (value, t) => {
  if (!value) {
    return '';
  }
  const diffMs = Date.now() - new Date(value).getTime();
  const days = Math.max(0, Math.floor(diffMs / 86400000));
  if (days <= 0) {
    return t('memoryUpdatedToday');
  }
  if (days === 1) {
    return t('memoryUpdatedYesterday');
  }
  return t('memoryUpdatedDaysAgo', { count: days });
};

export const MemorySummaryModal = ({ open, settings, onClose }) => {
  const { t } = useI18n();
  const dispatch = useAppDispatch();
  const [draft, setDraft] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setDraft(settings?.memorySummary || '');
    setError('');
    setMenuOpen(false);
  }, [open, settings?.memorySummary]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !confirmDisable) {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, confirmDisable, onClose]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    const onClick = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const count = draft.length;
  const overLimit = count > MEMORY_SUMMARY_MAX;
  const updatedLabel = useMemo(
    () => relativeUpdatedLabel(settings?.memorySummaryUpdatedAt, t),
    [settings?.memorySummaryUpdatedAt, t]
  );

  const handleSave = async () => {
    if (overLimit || saving) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      await updateMemorySettingsApi({ memorySummary: draft });
      await dispatch(fetchMemorySettings());
      onClose();
    } catch (requestError) {
      setError(requestError.message || t('memorySaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    setMenuOpen(false);
    setRefreshing(true);
    setError('');
    try {
      const result = await refreshMemorySummaryApi();
      setDraft(result.memorySummary || '');
      await dispatch(fetchMemorySettings());
    } catch (requestError) {
      setError(requestError.message || t('memorySummaryRefreshError'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteAndDisable = async () => {
    setSaving(true);
    setError('');
    try {
      await deleteAllMemoriesApi({ disable: true });
      await dispatch(fetchMemorySettings());
      setConfirmDisable(false);
      onClose();
    } catch (requestError) {
      setError(requestError.message || t('memoryClearError'));
    } finally {
      setSaving(false);
    }
  };

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div data-risman-modal="" className="fixed inset-0 z-[70] flex items-center justify-center bg-neutral-950/50 p-4">
      <div className="flex w-full max-w-2xl flex-col rounded-3xl bg-white p-5 shadow-2xl dark:bg-neutral-950">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-neutral-950 dark:text-white">{t('memorySummaryTitle')}</h2>
            {updatedLabel ? <p className="mt-0.5 text-xs text-neutral-400">{updatedLabel}</p> : null}
          </div>
          <div className="flex items-center gap-1">
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                aria-label={t('memoryMore')}
                onClick={() => setMenuOpen((value) => !value)}
              >
                <MoreHorizontal size={18} />
              </button>
              {menuOpen ? (
                <div className="absolute start-0 top-10 z-10 w-64 rounded-xl border border-neutral-200 bg-white py-1 shadow-lg dark:border-neutral-800 dark:bg-neutral-950">
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-900"
                    onClick={() => {
                      setMenuOpen(false);
                      setAboutOpen(true);
                    }}
                  >
                    <Info size={15} />
                    {t('memoryAbout')}
                  </button>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-900"
                    onClick={handleRefresh}
                    disabled={refreshing}
                  >
                    <RefreshCw size={15} className={classNames(refreshing && 'animate-spin')} />
                    {t('memoryUpdateSummary')}
                  </button>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10"
                    onClick={() => {
                      setMenuOpen(false);
                      setConfirmDisable(true);
                    }}
                  >
                    <Trash2 size={15} />
                    {t('memoryDeleteAndDisable')}
                  </button>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-900"
              onClick={onClose}
              aria-label={t('close')}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {error ? (
          <p role="alert" className="mb-3 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-200">
            {error}
          </p>
        ) : null}

        {aboutOpen ? (
          <p className="mb-3 rounded-xl bg-neutral-50 px-3 py-2 text-sm text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300">
            {t('memoryTooltip')}
          </p>
        ) : null}

        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className="min-h-[240px] w-full rounded-2xl bg-neutral-100 px-4 py-3 text-sm leading-7 text-neutral-800 outline-none dark:bg-neutral-900 dark:text-neutral-100"
          maxLength={MEMORY_SUMMARY_MAX + 40}
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <p className={classNames('text-xs', overLimit ? 'text-rose-600' : 'text-neutral-400')}>
            {count} / {MEMORY_SUMMARY_MAX}
          </p>
          <Button type="button" onClick={handleSave} disabled={saving || overLimit}>
            {saving ? <LoaderCircle size={16} className="animate-spin" /> : null}
            {t('save')}
          </Button>
        </div>
      </div>
      <ConfirmDangerDialog
        open={confirmDisable}
        title={t('memoryDeleteAndDisable')}
        message={t('memoryDeleteAndDisableConfirm')}
        confirmLabel={t('memoryClear')}
        loading={saving}
        onClose={() => setConfirmDisable(false)}
        onConfirm={handleDeleteAndDisable}
      />
    </div>,
    document.body
  );
};
